/**
 * Customize drawer for the Rules page.
 *
 * Right-side slide-in drawer (React Portal to document.body so it
 * floats above the sticky header and side panel). Three sections:
 *   - Sort: Sort by (field) + Direction (asc/desc)
 *   - Rows: Rows per page + Row height (comfortable/compact)
 *   - Columns: Show/hide checkboxes + Move up/down reorder
 *
 * Reorder uses up/down buttons (not HTML5 drag) in PR 9: more
 * accessible, works on mobile, no library needed. A follow-up PR
 * can add drag-and-drop with @dnd-kit if we need multi-select
 * reorder or keyboard-friendly drag.
 *
 * All changes persist immediately via useRulesCustomization
 * (localStorage keyed by tab) so a tweak survives reloads.
 *
 * Close via: X button, backdrop click, Escape key, or page nav.
 */

import { useCallback, useEffect, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { X, ChevronUp, ChevronDown } from "lucide-react";
import type {
  RulesCustomization,
  RuleColumnId,
  RuleSortField,
  RuleSortDirection,
  RulePageSize,
  RuleRowDensity,
} from "../rulesTypes";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  bgFade: "#F3F5F5",
};

interface CustomizeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  customization: RulesCustomization;
  onUpdate: (patch: Partial<RulesCustomization>) => void;
  onToggleColumn: (columnId: RuleColumnId) => void;
  onReorderColumns: (orderedIds: RuleColumnId[]) => void;
  onReset: () => void;
}

const SORT_OPTIONS: Array<{ value: RuleSortField; label: string }> = [
  { value: "priority", label: "Priority" },
  { value: "name", label: "Rule name" },
  { value: "lastApplied", label: "Last applied" },
  { value: "matchCount", label: "Match count" },
];

const PAGE_SIZE_OPTIONS: RulePageSize[] = [25, 50, 100, 200];

export function CustomizeDrawer({
  isOpen,
  onClose,
  customization,
  onUpdate,
  onToggleColumn,
  onReorderColumns,
  onReset,
}: CustomizeDrawerProps) {
  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const moveColumn = useCallback(
    (columnId: RuleColumnId, direction: -1 | 1) => {
      const ids = customization.columns.map((c) => c.id);
      const idx = ids.indexOf(columnId);
      if (idx === -1) return;
      const nextIdx = idx + direction;
      if (nextIdx < 0 || nextIdx >= ids.length) return;
      const reordered = [...ids];
      [reordered[idx], reordered[nextIdx]] = [reordered[nextIdx], reordered[idx]];
      onReorderColumns(reordered);
    },
    [customization.columns, onReorderColumns],
  );

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        style={overlayStyle}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        style={drawerStyle}
        role="dialog"
        aria-label="Customize table"
        aria-modal="true"
      >
        <div style={headStyle}>
          <h2 style={titleStyle}>Customize</h2>
          <button
            type="button"
            onClick={onClose}
            style={closeBtnStyle}
            aria-label="Close customize drawer"
          >
            <X size={18} color={PALETTE.ink3} />
          </button>
        </div>

        <div style={bodyStyle}>
          <Section title="Sort">
            <Field label="Sort by">
              <Select
                value={customization.sortBy}
                onChange={(value) => onUpdate({ sortBy: value as RuleSortField })}
                options={SORT_OPTIONS.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
            </Field>
            <Field label="Direction">
              <Select
                value={customization.sortDirection}
                onChange={(value) =>
                  onUpdate({ sortDirection: value as RuleSortDirection })
                }
                options={[
                  { value: "asc", label: "Ascending" },
                  { value: "desc", label: "Descending" },
                ]}
              />
            </Field>
          </Section>

          <Section title="Rows">
            <Field label="Rows per page">
              <Select
                value={String(customization.pageSize)}
                onChange={(value) =>
                  onUpdate({ pageSize: Number(value) as RulePageSize })
                }
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  value: String(n),
                  label: String(n),
                }))}
              />
            </Field>
            <Field label="Row height">
              <RadioRow
                checked={customization.rowDensity === "comfortable"}
                onClick={() =>
                  onUpdate({ rowDensity: "comfortable" as RuleRowDensity })
                }
                label="Comfortable"
              />
              <RadioRow
                checked={customization.rowDensity === "compact"}
                onClick={() =>
                  onUpdate({ rowDensity: "compact" as RuleRowDensity })
                }
                label="Compact"
              />
            </Field>
          </Section>

          <Section title="Columns">
            <div style={columnsHintStyle}>
              Use the arrows to reorder. Click a column to show or hide.
            </div>
            {customization.columns.map((column, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === customization.columns.length - 1;
              return (
                <div
                  key={column.id}
                  style={{
                    ...columnRowStyle,
                    opacity: column.visible ? 1 : 0.55,
                  }}
                >
                  <div style={columnArrowsStyle}>
                    <button
                      type="button"
                      disabled={isFirst}
                      onClick={() => moveColumn(column.id, -1)}
                      style={{
                        ...arrowBtnStyle,
                        opacity: isFirst ? 0.3 : 1,
                        cursor: isFirst ? "not-allowed" : "pointer",
                      }}
                      aria-label={`Move ${column.label} up`}
                    >
                      <ChevronUp size={12} color={PALETTE.ink3} />
                    </button>
                    <button
                      type="button"
                      disabled={isLast}
                      onClick={() => moveColumn(column.id, 1)}
                      style={{
                        ...arrowBtnStyle,
                        opacity: isLast ? 0.3 : 1,
                        cursor: isLast ? "not-allowed" : "pointer",
                      }}
                      aria-label={`Move ${column.label} down`}
                    >
                      <ChevronDown size={12} color={PALETTE.ink3} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggleColumn(column.id)}
                    style={columnToggleBtnStyle}
                    aria-pressed={column.visible}
                  >
                    <span
                      style={{
                        ...columnCheckStyle,
                        background: column.visible ? PALETTE.ink : "#ffffff",
                        borderColor: column.visible ? PALETTE.ink : PALETTE.line,
                      }}
                    >
                      {column.visible && (
                        <span style={{ color: "#ffffff", fontSize: 10, fontWeight: 700 }}>
                          ✓
                        </span>
                      )}
                    </span>
                    <span style={columnLabelStyle}>{column.label}</span>
                  </button>
                </div>
              );
            })}
          </Section>

          <button type="button" onClick={onReset} style={resetBtnStyle}>
            Reset to default
          </button>
        </div>
      </aside>
    </>,
    document.body,
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={sectionStyle}>
      <div style={sectionTitleStyle}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={fieldStyle}>
      <label style={fieldLabelStyle}>{label}</label>
      {children}
    </div>
  );
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}

function Select({ value, onChange, options }: SelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={selectStyle}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

interface RadioRowProps {
  checked: boolean;
  onClick: () => void;
  label: string;
}

function RadioRow({ checked, onClick, label }: RadioRowProps) {
  return (
    <button type="button" onClick={onClick} style={radioRowStyle}>
      <span
        style={{
          width: 16,
          height: 16,
          border: `${checked ? 2 : 1.5}px solid ${checked ? PALETTE.ink : PALETTE.line}`,
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {checked && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: PALETTE.ink,
            }}
          />
        )}
      </span>
      <span style={{ fontSize: 13, color: PALETTE.ink }}>{label}</span>
    </button>
  );
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(14, 26, 26, 0.14)",
  zIndex: 1500,
};

const drawerStyle: CSSProperties = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  width: 360,
  maxWidth: "100vw",
  background: "#ffffff",
  borderLeft: `1px solid ${PALETTE.line}`,
  boxShadow: "-8px 0 20px -4px rgba(14, 26, 26, 0.08)",
  zIndex: 1501,
  display: "flex",
  flexDirection: "column",
};

const headStyle: CSSProperties = {
  padding: "18px 20px",
  borderBottom: `1px solid ${PALETTE.line}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const titleStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: PALETTE.ink,
  margin: 0,
};

const closeBtnStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  width: 28,
  height: 28,
  borderRadius: 6,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

const bodyStyle: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "18px 20px 32px",
};

const sectionStyle: CSSProperties = {
  marginBottom: 24,
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: PALETTE.ink,
  marginBottom: 10,
};

const fieldStyle: CSSProperties = {
  marginBottom: 10,
  display: "flex",
  flexDirection: "column",
};

const fieldLabelStyle: CSSProperties = {
  fontSize: 11,
  color: PALETTE.ink3,
  fontWeight: 500,
  marginBottom: 5,
};

const selectStyle: CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 6,
  fontSize: 13,
  color: PALETTE.ink,
  background: "#ffffff",
  outline: "none",
  fontFamily: "inherit",
  cursor: "pointer",
};

const radioRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 0",
  background: "transparent",
  border: 0,
  cursor: "pointer",
  width: "100%",
  textAlign: "left",
  fontFamily: "inherit",
};

const columnsHintStyle: CSSProperties = {
  fontSize: 11.5,
  color: PALETTE.ink4,
  marginBottom: 10,
};

const columnRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "6px 0",
  borderRadius: 6,
};

const columnArrowsStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  flexShrink: 0,
};

const arrowBtnStyle: CSSProperties = {
  background: "transparent",
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 4,
  width: 20,
  height: 16,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};

const columnToggleBtnStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flex: 1,
  padding: "8px 10px",
  background: "transparent",
  border: 0,
  borderRadius: 6,
  cursor: "pointer",
  fontFamily: "inherit",
  textAlign: "left",
};

const columnCheckStyle: CSSProperties = {
  width: 15,
  height: 15,
  border: `1.5px solid`,
  borderRadius: 3,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const columnLabelStyle: CSSProperties = {
  flex: 1,
  fontSize: 13,
  color: PALETTE.ink,
  fontWeight: 500,
};

const resetBtnStyle: CSSProperties = {
  background: "transparent",
  border: `1px solid ${PALETTE.line}`,
  color: PALETTE.ink2,
  fontSize: 12.5,
  fontWeight: 500,
  padding: "8px 12px",
  borderRadius: 8,
  cursor: "pointer",
  marginTop: 10,
  fontFamily: "inherit",
  width: "100%",
};